import { Failure } from '@mrhenry/failure';
import { html, TemplateResult } from 'lit';
import { live } from 'lit/directives/live.js';
import { classMap } from 'lit/directives/class-map.js';
import { NumberOfNights, WeekData } from '../../../types/booking-data';
import { Dispatcher } from '../../../types/booking-request';
import { staticText } from '../../../static-text';

const optionsNights = [
	7,
	14,
	21,
	28,
];

export function numberOfNightsSelect(
	startOfStay: Date | null,
	houseId: number,
	numberOfNights: NumberOfNights,
	weekData: WeekData | null,
	dispatch: Dispatcher,
	assetUrl: string,
	numberOfNightsFailure?: Failure
): TemplateResult {
	const changeHandler = async( e: InputEvent ) => {
		if ( !( e.target instanceof HTMLSelectElement ) ) {
			throw new Error( 'e.target should be instanceof HTMLSelectElement' );
		}

		const value = parseInt( e.target.value, 10 );
		if ( isNaN( value ) ) {
			dispatch( {
				type: 'UPDATE_NUMBER_OF_NIGHTS_FAILURE',
				numberOfNightsFailure: Failure.from( staticText.numberOfNightsNotANumberFailureMessage ),
			} );

			return;
		}

		if (
			7 !== value &&
			14 !== value &&
			21 !== value &&
			28 !== value
		) {
			dispatch( {
				type: 'UPDATE_NUMBER_OF_NIGHTS_FAILURE',
				numberOfNightsFailure: Failure.from( staticText.numberOfNightsUnknownValueFailureMessage ),
			} );

			return;
		}

		dispatch( {
			type: 'UPDATE_NUMBER_OF_NIGHTS',
			numberOfNights: value,
		} );
	};

	const options = optionsNights.map( ( option ) => {
		return html`
			<option
				value=${option}
				?selected=${option === numberOfNights}
			>
				${option}
			</option>
		`;
	} );

	const errorMessageHTML: TemplateResult[] = [];
	if ( Failure.isFailure( numberOfNightsFailure ) ) {
		errorMessageHTML.push( html`
			<p class="form__field-validation-error theme-color-error">
				<img alt="" class="form__field-validation-error__icon" src="${assetUrl}/assets/images/alert-circle.svg">
				${numberOfNightsFailure?.message}
			</p>
		` );
	}

	let ariaLive = 'off';
	const aria: TemplateResult[] = [];

	if ( weekData ) {
		weekData.weeks.forEach( ( week, index ) => {
			// The first week is going to be evaluated by the start of stay input, no need to add double error messages and annoy the user with this.
			if ( 0 === index ) {
				return;
			}

			if ( !week.priceInEuroCents || week.availability === 'NOT_AVAILABLE' ) {
				errorMessageHTML.push( html`
					<p class="form__field-validation-error theme-color-error">
						<img alt="" class="form__field-validation-error__icon" src="${assetUrl}/assets/images/alert-circle.svg">
						${staticText.dateIsNotAvailableErrorMessage( week )}
					</p>
				` );

				return;
			}

			switch ( week.availability ) {
				case 'IS_BOOKED':
					errorMessageHTML.push( html`
						<p class="form__field-validation-error theme-color-error">
							<img alt="" class="form__field-validation-error__icon" src="${assetUrl}/assets/images/alert-circle.svg">
							${staticText.dateIsBookedErrorMessage( week )}
						</p>
					` );

					break;
				case 'HAS_OPTION':
					ariaLive = 'polite';
					aria.push( html`
						<p class="form__field-validation-feedback theme-color-orange">
							${staticText.dateHasAnOptionErrorMessage( week )}
						</p>
					` );

					break;
				case 'AVAILABLE':
				default:
					break;
			}
		} );
	}

	const classes = {
		'has-error': 0 < errorMessageHTML.length,
		'has-feedback': 0 < aria.length,
	};

	return html`
		<div class="form__row ${classMap( classes )}">
			<div class="form__label-container type-d">
				<label class="form__label" for="number-of-nights">
					${staticText.numberOfNights}
				</label>
			</div>

			<select
				.disabled=${!startOfStay || !weekData}
				@change=${changeHandler}
				class="form__select"
				id="number-of-nights"
				name="number-of-nights"
			>
				${options}
			</select>

			<div aria-live=${live( ariaLive )}>
				${aria}
			</div>

			${errorMessageHTML}
		</div>
	`;
}
